// @ts-nocheck
import React, { useMemo } from 'react';

import getLocationDisplayName from '@staty/shared/utils/getLocationDisplayName';
import { formatTimeLabel } from '@staty/shared/utils/gameDateTime';

import type { ResolvedGameRow } from '../bulkImport/types';

// Visual constants kept consistent with TournamentSchedulerDay2Admin for familiarity.
const SLOT_HEIGHT = 22;
const SNAP_MINUTES = 15;
const TIME_GUTTER_WIDTH = 80;
const COLUMN_WIDTH = 240;

// Stable, accessible color cycle for section badges.
const SECTION_COLORS = [
  'bg-sky-100 border-sky-300 text-sky-900',
  'bg-emerald-100 border-emerald-300 text-emerald-900',
  'bg-amber-100 border-amber-300 text-amber-900',
  'bg-violet-100 border-violet-300 text-violet-900',
  'bg-rose-100 border-rose-300 text-rose-900',
  'bg-cyan-100 border-cyan-300 text-cyan-900',
];

const colorForSection = (name: string) => {
  if (!name) return SECTION_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash + name.charCodeAt(i)) % SECTION_COLORS.length;
  return SECTION_COLORS[hash];
};

const FREE_TEXT_LOCATION_KEY = '__free_text__';

interface Props {
  rows: ResolvedGameRow[];
  locations: any[];
}

export default function BulkImportPreviewCalendar({ rows, locations }: Props) {
  // Only saveable rows: include "ready" and "warning"; skip "error".
  const saveable = useMemo(() => rows.filter((r) => r.status !== 'error'), [rows]);

  // Group by dateKey, then by location column.
  const grouped = useMemo(() => {
    const byDate: Record<string, ResolvedGameRow[]> = {};
    saveable.forEach((r) => {
      const key = r.display.dateKey || 'unscheduled';
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(r);
    });
    return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
  }, [saveable]);

  // Location columns: every known location used by any row, plus an optional
  // trailing "Free-text location" column for rows whose Location didn't resolve.
  const columns = useMemo(() => {
    const usedIds = new Set<string>();
    let hasFreeText = false;
    saveable.forEach((r) => {
      if (r.payload.location) {
        usedIds.add(r.payload.location);
      } else if (r.display.locationName) {
        hasFreeText = true;
      }
    });
    const cols = locations
      .filter((loc) => usedIds.has(loc._id))
      .map((loc) => ({ key: loc._id, label: getLocationDisplayName(loc) }));
    if (hasFreeText) {
      cols.push({ key: FREE_TEXT_LOCATION_KEY, label: 'Free-text location' });
    }
    return cols;
  }, [saveable, locations]);

  if (saveable.length === 0) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm text-muted-foreground">
        No previewable games. Upload a CSV with valid rows to see them here.
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm text-muted-foreground">
        Rows are valid but have no matching locations to render columns for.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {grouped.map(([dateKey, dayRows]) => {
        // Compute time bounds for this day.
        const startsAndEnds = dayRows.map((r) => ({
          start: r.display.startMinutes,
          end: r.display.startMinutes + (r.payload.duration || 60),
        }));
        const minStart = Math.min(...startsAndEnds.map((s) => s.start));
        const maxEnd = Math.max(...startsAndEnds.map((s) => s.end));
        const padded = {
          start: Math.max(0, Math.floor((minStart - 30) / 60) * 60),
          end: Math.min(24 * 60, Math.ceil((maxEnd + 30) / 60) * 60),
        };
        const totalHeight = ((padded.end - padded.start) / SNAP_MINUTES) * SLOT_HEIGHT;

        const ticks: number[] = [];
        for (let m = padded.start; m <= padded.end; m += SNAP_MINUTES) ticks.push(m);

        return (
          <div key={dateKey}>
            <div className="mb-2 text-sm font-semibold text-foreground">
              {dateKey === 'unscheduled' ? 'Unscheduled' : formatDateHeading(dateKey)}
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {dayRows.length} game{dayRows.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="overflow-x-auto">
              <div
                className="relative min-w-max rounded-md border bg-card"
                style={{ width: TIME_GUTTER_WIDTH + columns.length * COLUMN_WIDTH }}
              >
                {/* Header */}
                <div className="sticky top-0 z-10 flex border-b bg-card">
                  <div
                    style={{ width: TIME_GUTTER_WIDTH }}
                    className="border-r px-2 py-2 text-xs font-semibold text-muted-foreground"
                  >
                    Time
                  </div>
                  {columns.map((col) => (
                    <div
                      key={col.key}
                      style={{ width: COLUMN_WIDTH }}
                      className="border-r px-3 py-2 text-sm font-semibold"
                    >
                      {col.label}
                    </div>
                  ))}
                </div>

                {/* Body */}
                <div className="relative flex">
                  {/* Time gutter */}
                  <div
                    style={{ width: TIME_GUTTER_WIDTH, height: totalHeight }}
                    className="relative border-r bg-muted/50"
                  >
                    {ticks.map((minute) => {
                      const top = ((minute - padded.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                      const isHour = minute % 60 === 0;
                      return (
                        <div key={`label-${minute}`} className="absolute left-0 right-0" style={{ top }}>
                          {isHour ? (
                            <span className="absolute -top-2 left-2 text-[11px] text-muted-foreground">
                              {formatTimeLabel(minute)}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  {/* Location columns */}
                  {columns.map((col) => {
                    const colRows = dayRows.filter((r) => {
                      if (col.key === FREE_TEXT_LOCATION_KEY) return !r.payload.location && r.display.locationName;
                      return r.payload.location === col.key;
                    });
                    return (
                      <div
                        key={col.key}
                        style={{ width: COLUMN_WIDTH, height: totalHeight }}
                        className="relative border-r"
                      >
                        {ticks.map((minute) => {
                          const top = ((minute - padded.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                          const isHour = minute % 60 === 0;
                          return (
                            <div
                              key={`line-${col.key}-${minute}`}
                              className={`absolute left-0 right-0 border-t ${isHour ? 'border-input' : 'border-border'}`}
                              style={{ top }}
                            />
                          );
                        })}

                        {colRows.map((row, idx) => {
                          const top =
                            ((row.display.startMinutes - padded.start) / SNAP_MINUTES) * SLOT_HEIGHT;
                          const height = ((row.payload.duration || 60) / SNAP_MINUTES) * SLOT_HEIGHT;
                          const color = colorForSection(row.display.sectionName);
                          return (
                            <div
                              key={`game-${row.raw.rowIndex}-${idx}`}
                              className={`absolute left-1 right-1 overflow-hidden rounded border px-2 py-1 text-[11px] shadow-sm ${color}`}
                              style={{ top, height: Math.max(height - 2, 18) }}
                              title={`${row.display.divisionName} · ${row.display.homeTeamName} vs ${row.display.awayTeamName}`}
                            >
                              <div className="truncate font-semibold">
                                {row.display.homeTeamName} vs {row.display.awayTeamName}
                              </div>
                              <div className="truncate opacity-80">
                                {row.display.divisionName}
                                {row.display.sectionName ? ` · ${row.display.sectionName}` : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDateHeading(dateKey: string) {
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
}
